import React, { memo, useState, useCallback } from 'react'
import cn from 'classnames'
import { FormattedHTMLMessage, Sandbox, Spinner } from '@web/walter-ui-kit'
import { commonPosthogEvents } from '@walter/shared/constants/posthog'
import { logPosthogEvent } from '@walter/shared/utils/posthog'
import SnsWebSdk from '@sumsub/websdk'

import { HttpError } from '@web/walter-lib/api'
import { SnsError } from '@sumsub/websdk/types/types'
import { useTheme } from '@web/walter-lib/theme'
import { useLocale } from '@web/walter-lib/locale'
import styles from './style.module.css'
import { lang } from './lang'
import { regApi } from '~api'
import { processErrorResponse } from '~hooks/usePromise/utils'

interface UseSumSubProps {
    token?: string
    isNight: boolean
    lang: string
    onGetNewToken: () => Promise<string>
    onApplicantSubmit?: () => void
    setIsHelpersShown: (show: boolean) => void
}

interface LaunchSdkProps extends UseSumSubProps {
    snsWebSdk: typeof SnsWebSdk
}

const scriptSrc = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js'
const containerId = 'sumsub-websdk-container'

const processSumSubError = (error: SnsError & { msg?: string }) => {
    if (error instanceof HttpError) {
        return processErrorResponse(error) || error.message || error.code
    }

    return error.msg
}

function launchSdk({
    snsWebSdk,
    lang,
    isNight,
    token,
    // setHeight,
    onApplicantSubmit,
    setIsHelpersShown,
    onGetNewToken,
}: LaunchSdkProps) {
    const snsWebSdkInstance = snsWebSdk
        .init(token || '', () => onGetNewToken())
        .withConf({
            lang,
            theme: isNight ? 'dark' : 'light',
        })
        .withOptions({
            adaptIframeHeight: false,
        })
        .on('idCheck.onResize', () => {
            // setHeight(height)
        })
        .on('idCheck.onApplicantSubmitted', () => {
            logPosthogEvent(commonPosthogEvents.kycDataSubmitted, { verification_type: 'web' })
            onApplicantSubmit?.()
        })
        .on('idCheck.onApplicantResubmitted', () => {
            onApplicantSubmit?.()
        })
        .on('idCheck.onApplicantLoaded', () => {
            setIsHelpersShown(false)
        })
        .on('idCheck.onError', error => {
            logPosthogEvent(commonPosthogEvents.kycError, {
                screen: window.location.href,
                message: processSumSubError(error),
            })
        })
        .on('idCheck.onUploadError', error => {
            logPosthogEvent(commonPosthogEvents.kycError, {
                screen: window.location.href,
                message: processSumSubError(error),
            })
        })
        .build()
    snsWebSdkInstance.launch(`#${containerId}`)
}

/** Use sumsub */
const useSumSubCb = ({ token, lang, isNight, setIsHelpersShown, onApplicantSubmit, onGetNewToken }: UseSumSubProps) =>
    useCallback(
        (iframeContentWindow: Window) => {
            if (!token) {
                return
            }

            const div = iframeContentWindow.document.createElement('div')
            div.id = containerId
            iframeContentWindow.document.body.appendChild(div)
            const { snsWebSdk } = iframeContentWindow

            launchSdk({
                snsWebSdk,
                lang,
                token,
                isNight,
                onApplicantSubmit,
                setIsHelpersShown,
                onGetNewToken,
            })
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [token]
    )

interface Props {
    token?: string
    helpersClassname?: string
    nextLevel?: number
    onApplicantSubmit?: () => void
}

function SumSub({ token, nextLevel, onApplicantSubmit, helpersClassname }: Props) {
    const { locale } = useLocale()
    const { isNight } = useTheme()
    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)
    const [isHelpersShown, setIsHelpersShown] = useState(true)

    const onGetNewToken = async () => {
        if (nextLevel) {
            const res = await regApi.getAccessToken({ nextLevel })
            return res.token || ''
        }
        return ''
    }

    const onScriptLoad = useSumSubCb({
        token,
        lang: locale,
        isNight,
        onApplicantSubmit,
        setIsHelpersShown,
        onGetNewToken,
    })
    const onScriptError = useCallback(() => {
        setIsLoading(false)
        setIsError(true)
    }, [])

    return (
        <div className={cn(styles.container, isHelpersShown && helpersClassname)}>
            {isHelpersShown && (
                <div className={styles.center}>
                    {isError && <FormattedHTMLMessage {...lang.failed} />}
                    {isLoading && <Spinner size={36} />}
                </div>
            )}
            <Sandbox
                onScriptLoad={onScriptLoad}
                onScriptError={onScriptError}
                scriptSrcToLoad={scriptSrc}
                isAllowSameOrigin
                isAllowPopups
                isAllowForms
                isVisible
                className={cn(styles.wrap, isHelpersShown && styles.center)}
                // height={height}
            />
        </div>
    )
}

export default memo(SumSub)
